import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/core/utils";

const alertVariants = cva(
  "group/alert flex w-full items-start gap-3 rounded-md border bg-card text-left text-sm [border-left-width:3px] [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        neutral: "border-l-ink-300 [&>svg]:text-muted-foreground",
        info: "border-l-info [&>svg]:text-info",
        warning: "border-l-warning [&>svg]:text-warning",
        danger:
          "border-l-danger [&>svg]:text-danger *:data-[slot=alert-description]:text-danger/90",
      },
      compact: {
        true: "px-3 py-2",
        false: "px-4 py-3",
      },
    },
    defaultVariants: {
      variant: "neutral",
      compact: false,
    },
  },
);

function Alert({
  className,
  variant,
  compact,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant, compact }), className)}
      {...props}
    />
  );
}

function AlertContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-content"
      className={cn("flex min-w-0 flex-1 flex-col gap-0.5", className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "text-[13px] leading-snug font-medium text-foreground [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "min-w-0 flex-1 text-[12px] leading-snug text-muted-foreground [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("shrink-0 self-center", className)}
      {...props}
    />
  );
}

export { Alert, AlertContent, AlertTitle, AlertDescription, AlertAction };
